/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useReducer, useRef } from 'react';

import cn from 'classnames';

import styles from './EnrichmentDemoPanel.module.scss';

const FIELD_LABELS = [
  ['legal', 'Legal name'],
  ['reg', 'Registration nr'],
  ['status', 'Status'],
  ['address', 'Registered address'],
  ['industry', 'Industry'],
  ['revenue', 'Filed revenue'],
  ['officers', 'Officers'],
] as const;

const COMPANIES = [
  {
    input: 'revolut.com',
    chip: 'Domain',
    grade: 'A',
    registry: 'Companies House',
    country: 'UK',
    matched: 'REVOLUT LTD',
    fields: {
      legal: 'REVOLUT LTD',
      reg: '08804411',
      status: 'Active',
      address: '7 Westferry Circus, London',
      industry: 'SIC 64110 · Banking',
      revenue: '£922.5M · FY23',
      officers: '9 active · 1 PSC',
    },
  },
  {
    input: '775 670 417',
    chip: 'Registration nr',
    grade: 'A',
    registry: 'RCS Paris',
    country: 'France',
    matched: 'LVMH SE',
    fields: {
      legal: 'LVMH MOËT HENNESSY LOUIS VUITTON SE',
      reg: '775 670 417 · RCS Paris',
      status: 'Active',
      address: '22 Av. Montaigne, Paris',
      industry: 'NAF 70.10Z · Holding',
      revenue: '€86.2B · FY23',
      officers: '16 board members',
    },
  },
  {
    input: 'GB260423730',
    chip: 'VAT number',
    grade: 'A',
    registry: 'Companies House',
    country: 'UK',
    matched: 'GLOBAL DATA INTELLIGENCE LTD',
    fields: {
      legal: 'GLOBAL DATA INTELLIGENCE LIMITED',
      reg: '09410808',
      status: 'Active',
      address: "Artisans' House, 7 Queensbridge, Northampton",
      industry: 'Data & company intelligence',
      revenue: 'Accounts filed · CH',
      officers: 'N. Buldumac · Director',
    },
  },
  {
    input: 'linkedin.com/company/adidas',
    chip: 'LinkedIn URL',
    grade: 'A',
    registry: 'Handelsregister',
    country: 'Germany',
    matched: 'adidas AG',
    fields: {
      legal: 'adidas AG',
      reg: 'HRB 3033 · Fürth',
      status: 'Active',
      address: 'Adi-Dassler-Str. 1, Herzogenaurach',
      industry: 'WZ 32.30 · Sporting goods',
      revenue: '€21.4B · FY23',
      officers: '6 executive board',
    },
  },
] as const;

type Phase = 'typing' | 'matched';

interface DemoState {
  companyIndex: number;
  typedLength: number;
  showChip: boolean;
  phase: Phase;
  visibleRows: number;
}

type DemoAction =
  | { type: 'reset'; index: number }
  | { type: 'type'; length: number }
  | { type: 'chip/show' }
  | { type: 'phase'; phase: Phase }
  | { type: 'row'; count: number };

const INITIAL_STATE: DemoState = {
  companyIndex: 0,
  typedLength: 0,
  showChip: false,
  phase: 'typing',
  visibleRows: 0,
};

function demoReducer(state: DemoState, action: DemoAction): DemoState {
  switch (action.type) {
    case 'reset':
      return { companyIndex: action.index, typedLength: 0, showChip: false, phase: 'typing', visibleRows: 0 };
    case 'type':
      return { ...state, typedLength: action.length };
    case 'chip/show':
      return { ...state, showChip: true };
    case 'phase':
      return { ...state, phase: action.phase };
    case 'row':
      return { ...state, visibleRows: action.count };
    default:
      return state;
  }
}

export const EnrichmentDemoPanel = () => {
  const [state, dispatch] = useReducer(demoReducer, INITIAL_STATE);
  const controlRef = useRef<{ goTo: (index: number) => void } | null>(null);

  useEffect(() => {
    const pending: ReturnType<typeof setTimeout>[] = [];

    const schedule = (fn: () => void, ms: number) => {
      pending.push(setTimeout(fn, ms));
    };

    const clearAll = () => {
      pending.forEach(clearTimeout);
      pending.length = 0;
    };

    const runCycle = (index: number) => {
      const company = COMPANIES[index];

      dispatch({ type: 'reset', index });

      let ch = 0;
      const typeNext = () => {
        dispatch({ type: 'type', length: ch });

        if (ch < company.input.length) {
          ch += 1;
          schedule(typeNext, 55);
          return;
        }

        dispatch({ type: 'chip/show' });
        schedule(() => {
          dispatch({ type: 'phase', phase: 'matched' });

          FIELD_LABELS.forEach((_, i) => {
            schedule(() => dispatch({ type: 'row', count: i + 1 }), 200 + i * 140);
          });
        }, 1200);
      };
      typeNext();

      const total = company.input.length * 55 + 1200 + 200 + FIELD_LABELS.length * 140 + 3400;
      schedule(() => runCycle((index + 1) % COMPANIES.length), total);
    };

    controlRef.current = {
      goTo: (index: number) => {
        clearAll();
        runCycle(index);
      },
    };

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      const company = COMPANIES[0];
      dispatch({ type: 'reset', index: 0 });
      dispatch({ type: 'type', length: company.input.length });
      dispatch({ type: 'chip/show' });
      dispatch({ type: 'phase', phase: 'matched' });
      dispatch({ type: 'row', count: FIELD_LABELS.length });
      return;
    }

    runCycle(0);
    return () => {
      clearAll();
      controlRef.current = null;
    };
  }, []);

  const company = COMPANIES[state.companyIndex];
  const matched = state.phase === 'matched';

  return (
    <div className={styles.root} aria-label="Live enrichment demonstration">
      <div className={styles.bar}>
        <span className={styles.barTitle}>
          <span className={styles.pulse} aria-hidden="true" />
          Data enrichment
        </span>
        <span className={styles.rowLabel}>Row {state.companyIndex + 1} of 12,400</span>
      </div>

      <div className={styles.body}>
        <div className={styles.input}>
          <span className={styles.inputLabel}>You send</span>
          <span className={styles.inputField}>
            <span className={styles.typed}>{company.input.slice(0, state.typedLength)}</span>
            {!matched && <span className={styles.caret} aria-hidden="true" />}
            <span className={cn(styles.chip, state.showChip && styles.on)}>{company.chip}</span>
          </span>
        </div>

        <div className={cn(styles.status, matched && styles.matched)}>
          <span className={styles.statusIcon} aria-hidden="true" />
          <span>
            {matched
              ? `Matched · ${company.matched} · Grade ${company.grade}`
              : 'Matching against 400+ government registries…'}
          </span>
        </div>

        <div className={styles.rows}>
          {FIELD_LABELS.map(([key, label], i) => (
            <div key={key} className={cn(styles.row, i < state.visibleRows && styles.on)}>
              <span className={styles.rowKey}>{label}</span>
              <span className={styles.rowValue}>
                <span className={styles.rowValueText}>{company.fields[key]}</span>
                <span className={styles.rowBadge} aria-hidden="true" />
              </span>
            </div>
          ))}
        </div>

        <div className={styles.source}>
          <span>{matched ? `Source → ${company.registry} · ${company.country}` : 'Source → —'}</span>
          <span className={styles.dots}>
            {COMPANIES.map((c, i) => (
              <button
                key={c.matched}
                type="button"
                className={cn(styles.dot, i === state.companyIndex && styles.on)}
                aria-label={`Show example ${i + 1}: ${c.matched}`}
                onClick={() => controlRef.current?.goTo(i)}
              />
            ))}
          </span>
        </div>
      </div>
    </div>
  );
};
